import React, { useState, useEffect } from 'react';
import { Phone, Zap, Star, Users, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BACKEND_URL } from '../config/api';

/**
 * OnCallProUpgrade Component — Now shows free On Call + Priority Boost info
 * Basic On Call is free. Priority Boost is earned through referrals.
 */
export default function OnCallProUpgrade({ 
  open, 
  onOpenChange, 
  onSubscribed,
  currentStatus = null 
}) {
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (open) fetchAccess();
  }, [open]);
  
  const fetchAccess = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/on-call/feature-access`, { withCredentials: true });
      setAccess(res.data);
    } catch {}
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="on-call-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-orange-500" />
            On Call
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-2">
          {/* Free On Call Info */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="on-call-free-info">
            <div className="flex items-center gap-2 mb-2">
              <Phone className="h-4 w-4 text-green-600" />
              <h3 className="font-semibold text-green-800">On Call is Free</h3>
            </div>
            <p className="text-green-700 text-sm leading-relaxed">
              Mark yourself On Call for free when you're available for urgent work. Employers will see you when they search for available trades.
            </p>
          </div>

          {/* Priority Boost section */}
          {access?.is_boosted ? (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-testid="priority-boost-active">
              <div className="flex items-center gap-2 mb-2">
                <Star className="h-4 w-4 text-orange-500 fill-orange-500" />
                <h3 className="font-semibold text-orange-800">Priority Boost Active</h3>
              </div>
              <p className="text-orange-700 text-sm">
                You appear higher in On Call search results until {access.boosted_until ? new Date(access.boosted_until).toLocaleDateString() : 'your boost expires'}.
              </p>
            </div>
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="priority-boost-info">
              <div className="flex items-center gap-2 mb-2">
                <Star className="h-4 w-4 text-slate-500" />
                <h3 className="font-semibold text-slate-700">Earn Priority Boost</h3>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed">
                Refer someone and get 1 month Priority Boost, helping you stand out when employers search On Call trades.
              </p>
            </div>
          )}

          {/* How it works */}
          <div className="space-y-2">
            <h4 className="font-medium text-slate-800 text-sm">How On Call works:</h4>
            <ul className="space-y-1.5">
              {[
                'Toggle On Call when you are available for urgent work',
                'Employers searching for urgent cover will see you first',
                'On Call is free — no subscription or payment needed',
                'Refer tradespeople to earn Priority Boost for enhanced visibility',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                  <ArrowRight className="h-3.5 w-3.5 text-orange-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <button onClick={() => { onOpenChange(false); if (onSubscribed) onSubscribed(); }}
            className="w-full py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition font-medium text-sm"
            data-testid="on-call-got-it-btn">
            Got it
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/**
 * OnCallProBadge — Now "Priority Boost" badge
 */
export function OnCallProBadge({ size = 'sm', isPriorityBoosted = false }) {
  if (!isPriorityBoosted) return null;
  return (
    <span className={`inline-flex items-center gap-1 bg-orange-100 border border-orange-300 text-orange-800 ${
      size === 'sm' ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs'
    } rounded-full font-bold`} data-testid="priority-boost-badge">
      <Star className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
      Priority
    </span>
  );
}


/**
 * OnCallProStatus — Shows On Call status with free/boost info
 */
export function OnCallProStatus({ status, onManage }) {
  return (
    <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-3 py-2" data-testid="on-call-status-card">
      <Phone className="h-4 w-4 text-green-600" />
      <div className="flex-1">
        <p className="text-sm font-medium text-green-800">On Call is Free</p>
        <p className="text-xs text-green-600">Toggle On Call anytime you're available</p>
      </div>
      {onManage && (
        <button onClick={onManage} className="text-xs text-green-700 hover:underline">
          Learn more
        </button>
      )}
    </div>
  );
}
